"use client";

import { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { Product } from "@/types/product.types";

import { ProductGallery } from "./ProductGallery";
import { ProductInfo } from "./ProductInfo";

// Lazy loaded components
const OrderDetails = dynamic(() => import("@/components/client/checkout/OrderDeatils").then(mod => mod.OrderDetails), {
    ssr: false,
    loading: () => <div className="h-40 w-full animate-pulse bg-gray-100 rounded-xl" />
});
const ProductReviews = dynamic(() => import("./ProductReviews").then(mod => mod.ProductReviews), {
    ssr: false,
    loading: () => <div className="h-20 w-full animate-pulse bg-gray-100 rounded-xl" />
});
const ProductTabs = dynamic(() => import("./ProductTabs").then(mod => mod.ProductTabs), {
    ssr: false
});
const ProductGridSection = dynamic(() => import("../landing/sections/ProductGridSection").then(mod => mod.ProductGridSection), {
    ssr: false
});
const QuickViewModal = dynamic(() => import("@/components/shared/model/QuickViewModal").then(mod => mod.QuickViewModal), {
    ssr: false
});

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbList,
} from "@/components/ui/breadcrumb";

import { useProductsByCategorySlug } from "@/features/categories/hook/useCategories";

interface Props {
    product: Product;
}

export const ProductDetailsPage = ({ product }: Props) => {
    // 🟠 Lifted state for synchronization
    const weightAttribute = product?.attributes?.find(
        (attr) => (attr.attribute.options ?? []).length > 0
    );
    console.log(weightAttribute);
    const weightOptions = weightAttribute?.attribute.options ?? [];
    const [optionQuantities, setOptionQuantities] = useState<Record<string, number>>({});

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [quantity, setQuantity] = useState(1);

    const handleQuickView = (item: Product) => {
        setSelectedProduct(item);
        setIsModalOpen(true);
    };

    // 🟢 Memo pour éviter recalcul inutile
    const galleryImages = useMemo(() => {
        if (!product.images) return [];
        return [...product.images]
            .sort((a, b) => {
                if (a.isPrimary) return -1;
                if (b.isPrimary) return 1;
                return (a.position || 0) - (b.position || 0);
            })
            .map((img) => img.url);
    }, [product.images]);

    // 🟢 React Query pour produits similaires
    const firstCategory = product.categories?.[0];
    const categorySlug = firstCategory?.category?.slug;

    const { data: categoryData, isLoading, error } = useProductsByCategorySlug(
        categorySlug || "",
        1,
        10,
        { enabled: !!categorySlug }
    );

    const similarProducts = useMemo(() => {
        return categoryData?.data?.filter((p) => p.id !== product.id) ?? [];
    }, [categoryData?.data, product.id]);

    return (
        <section className="bg-primary-50">
            <div className="container mx-auto px-4 py-10">
                {/* Breadcrumb removed for brevity, same as before */}
                <div className="mb-10">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/" className="text-gray-400">
                                    Accueil
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator className="text-gray-400" />
                            <BreadcrumbItem>
                                <BreadcrumbPage className="text-gray-900 font-semibold">
                                    {product.name}
                                </BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>

                {/* TOP GRID */}
                <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
                    <div className="space-y-4">
                        <ProductGallery images={galleryImages} />
                        <ProductReviews reviews={product.reviews || []} />
                    </div>

                    <div className="space-y-4">
                        <ProductInfo
                            product={product}
                            optionQuantities={optionQuantities}
                            setOptionQuantities={setOptionQuantities}
                            quantity={quantity}
                            setQuantity={setQuantity}
                        />
                        <OrderDetails
                            directProduct={product}
                            directOptionQuantities={optionQuantities}
                            setDirectOptionQuantities={setOptionQuantities}
                            directQuantity={quantity}
                            setDirectQuantity={setQuantity}
                        />
                    </div>
                </div>

                {/* DESCRIPTION */}
                <section className="mt-20">
                    <ProductTabs product={product} />
                </section>

                {/* SIMILAR PRODUCTS */}
                {!isLoading && !error && similarProducts.length > 0 && (
                    <ProductGridSection
                        title="Produits similaires"
                        products={similarProducts}
                        onQuickView={handleQuickView}
                        viewAllHref={categorySlug ? `/category/${categorySlug}/products` : "/products"}
                    />
                )}

                {/* QUICK VIEW MODAL */}
                {isModalOpen && (
                    <QuickViewModal
                        product={selectedProduct}
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                    />
                )}
            </div>
        </section>
    );
};